import { createContext, useCallback, useContext, useEffect, useRef } from 'react'
import type { ItineraryUpdate, EnrichmentCompleteEvent, HotelSearchResultsData, HotelSearchErrorData, ChatUiActionData, HotelMapViewData, ExperienceMapViewData } from 'tripPlanner/api/types'
import type { LocationInfoData } from 'tripPlanner/components/Chat/Message'
import type { OfferClickSearchParams } from './ChatMessageActionsContext'

// ── Event Types ──────────────────────────────────────────────────────
export type TripPlannerEvent =
  // Stream events (dispatched by TripPlannerChatProvider)
  | { type: 'itinerary_updates'; chatId: number; updates: Array<ItineraryUpdate> }
  | { type: 'enrichment_complete'; chatId: number; event: EnrichmentCompleteEvent }
  | { type: 'stream_complete'; chatId: number }
  | { type: 'hotel_search_results'; data: HotelSearchResultsData; generation: number }
  | { type: 'hotel_search_error'; data: HotelSearchErrorData; generation: number }
  | { type: 'ui_action'; data: ChatUiActionData }
  // Navigation (dispatched by orchestrator)
  | { type: 'reset_chat_state' }
  // Cross-container actions
  | { type: 'show_hotel_on_map'; data: HotelMapViewData }
  | { type: 'show_experience_on_map'; data: ExperienceMapViewData }
  | { type: 'show_explore_on_map'; coordinates: { lat: number; lng: number } }
  | { type: 'ask_ai_for_day'; dayNumber: number; title: string; date?: string }
  | { type: 'open_place_drawer'; googlePlaceId: string; placeName: string }
  | { type: 'open_location_drawer'; data: LocationInfoData }
  | { type: 'open_detail_drawer'; googlePlaceId: string; placeName: string; countryName: string; stopId?: number }
  | { type: 'open_offer_drawer'; offerId: string; searchParams?: OfferClickSearchParams; offerType?: string }
  | { type: 'itinerary_changed' }
  | { type: 'focus_chat_input' }

type EventHandler = (event: TripPlannerEvent) => void

interface TripPlannerEventBusValue {
  dispatch: (event: TripPlannerEvent) => void
  subscribe: (handler: EventHandler) => () => void
}

const TripPlannerEventBusContext = createContext<TripPlannerEventBusValue | null>(null)

export function TripPlannerEventBusProvider({ children }: { children: React.ReactNode }) {
  const subscribersRef = useRef(new Set<EventHandler>())

  const dispatch = useCallback((event: TripPlannerEvent) => {
    subscribersRef.current.forEach(handler => {
      try {
        handler(event)
      } catch(error) {
        console.error('[TripPlannerEventBus] Subscriber error:', error)
      }
    })
  }, [])

  const subscribe = useCallback((handler: EventHandler) => {
    subscribersRef.current.add(handler)
    return () => { subscribersRef.current.delete(handler) }
  }, [])

  // Value is stable — dispatch and subscribe never change
  const value = useRef({ dispatch, subscribe }).current

  return (
    <TripPlannerEventBusContext.Provider value={value}>
      {children}
    </TripPlannerEventBusContext.Provider>
  )
}

export function useTripPlannerEventBus() {
  const ctx = useContext(TripPlannerEventBusContext)
  if (!ctx) throw new Error('useTripPlannerEventBus must be within TripPlannerEventBusProvider')
  return ctx
}

/** Subscribe to specific event type(s). Handler is called only for matching events. */
export function useEventSubscription<T extends TripPlannerEvent['type']>(
  eventType: T | Array<T>,
  handler: (event: Extract<TripPlannerEvent, { type: T }>) => void,
) {
  const { subscribe } = useTripPlannerEventBus()
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  const stableTypes = useRef(eventType)
  stableTypes.current = eventType

  useEffect(() => {
    return subscribe((event) => {
      // ReadonlyArray<TripPlannerEvent['type']> widens T[] so .includes() accepts event.type
      const types: ReadonlyArray<TripPlannerEvent['type']> =
        Array.isArray(stableTypes.current) ? stableTypes.current : [stableTypes.current]
      if (types.includes(event.type)) {
        // TS cannot narrow discriminated unions through .includes() — this is the
        // standard workaround. The check above guarantees event.type is one of T.
        handlerRef.current(event as Extract<TripPlannerEvent, { type: T }>)
      }
    })
  }, [subscribe])
}
